export interface BorderRadii {
	topLeft: number;
	bottomLeft: number;
	topRight: number;
	bottomRight: number;
}

const getMaxEffectiveBorderRadius = (computedStyle: CSSStyleDeclaration) => {
	if (computedStyle.height === 'auto' || computedStyle.width === 'auto') return null;

	// Effective border radius in px cannot be higher than half of the smallest simension
	return Math.min(parseInt(computedStyle.height), parseInt(computedStyle.width)) / 2;
};

/**
 * Get border radii of element based from computed CSS
 *
 * @param element element to read determine additional padding for
 */
export const getComputedBorderRadii = (element: Element): BorderRadii => {
	const computedStyle = window.getComputedStyle(element);
	const maxEffectiveBorderRadius = getMaxEffectiveBorderRadius(computedStyle);
	if (maxEffectiveBorderRadius === null) return null;

	return {
		topLeft: Math.min(maxEffectiveBorderRadius, parseInt(computedStyle.borderTopLeftRadius)) || 0,
		topRight: Math.min(maxEffectiveBorderRadius, parseInt(computedStyle.borderTopRightRadius)) || 0,
		bottomRight: Math.min(maxEffectiveBorderRadius, parseInt(computedStyle.borderBottomRightRadius)) || 0,
		bottomLeft: Math.min(maxEffectiveBorderRadius, parseInt(computedStyle.borderBottomLeftRadius)) || 0,
	};
};

/**
 * Get border radii of element based from design tokens
 *
 * @param element element to read determine additional padding for
 */
export const getBorderRadiiFromTokens = (element: Element, tokenBase: string): BorderRadii => {
	const computedStyle = window.getComputedStyle(element);
	const tokenBorderRadius = computedStyle.getPropertyValue(`${tokenBase}-border-radius`);
	const maxEffectiveBorderRadius = getMaxEffectiveBorderRadius(computedStyle);
	if (tokenBorderRadius === '' || maxEffectiveBorderRadius === null) return null;

	/* Get general border radius token */
	const generalBorderRadiusParts = tokenBorderRadius
		.trim()
		.split(' ')
		.map((borderRadiusString) => parseInt(borderRadiusString) || 0);

	let generalBorderRadius;
	if (generalBorderRadiusParts.length === 1) {
		generalBorderRadius = {
			'top-left': generalBorderRadiusParts[0],
			'top-right': generalBorderRadiusParts[0],
			'bottom-right': generalBorderRadiusParts[0],
			'bottom-left': generalBorderRadiusParts[0],
		};
	} else if (generalBorderRadiusParts.length === 4) {
		generalBorderRadius = {
			'top-left': generalBorderRadiusParts[0],
			'top-right': generalBorderRadiusParts[1],
			'bottom-right': generalBorderRadiusParts[2],
			'bottom-left': generalBorderRadiusParts[3],
		};
	} else {
		generalBorderRadius = {
			'top-left': 0,
			'top-right': 0,
			'bottom-right': 0,
			'bottom-left': 0,
		};
	}

	/* Get border radius token per corner, with fallback to general border radius */
	const getBorderRadius = (cornerName: string): number => {
		const cornerValue = computedStyle.getPropertyValue(`${tokenBase}-border-${cornerName}-radius`);
		const usedValue = cornerValue === '' ? generalBorderRadius[cornerName] : parseInt(cornerValue);
		return Math.min(maxEffectiveBorderRadius, usedValue);
	};

	const borderRadii = {
		topLeft: getBorderRadius('top-left'),
		bottomLeft: getBorderRadius('bottom-left'),
		topRight: getBorderRadius('top-right'),
		bottomRight: getBorderRadius('bottom-right'),
	};
	return borderRadii;
};
